import { create } from 'zustand';
import { workspaceApi, type Workspace } from '../services/workspaceApi';

// BP 03 follow-up — single shared cache of "the workspaces I belong to"
// (GET /api/v1/workspaces), used by both the avatar menu's workspace
// switcher (WorkspaceMenuSection) and anything that needs to resolve the
// CURRENT workspace's display name (see frontend/src/lib/useActiveOrg.ts).
// One fetch per session, shared, instead of every consumer fetching its own
// copy.
//
// Deliberately NOT wrapped in zustand's `persist` middleware. Workspace
// membership is authorization-adjacent data — on a shared browser, a stale
// localStorage copy of user A's workspace list surviving into user B's
// session (before the fetch resolves) is exactly the bug class this file
// exists to fix, not one to reintroduce. It lives in memory only and is
// cleared by useAuth().logout() via reset().
interface WorkspaceState {
  workspaces: Workspace[];
  status: 'idle' | 'loading' | 'loaded' | 'error';
  /** Idempotent — safe to call from every consumer's mount effect. Only the
   *  first caller actually fires the request; later calls (before or after
   *  it resolves) are no-ops. */
  ensureLoaded: () => void;
  /** Clears the cache. Called from useAuth().logout() so a new sign-in
   *  never reads the previous account's workspace list. */
  reset: () => void;
}

export const useWorkspaceStore = create<WorkspaceState>((set, get) => ({
  workspaces: [],
  status: 'idle',
  ensureLoaded: () => {
    const { status } = get();
    if (status === 'loading' || status === 'loaded') return;
    set({ status: 'loading' });
    workspaceApi.listMine()
      .then(ws => set({ workspaces: ws, status: 'loaded' }))
      .catch(() => set({ status: 'error' }));
  },
  reset: () => set({ workspaces: [], status: 'idle' }),
}));
